import { recordRoomVisit } from '../place/hometabs/PassportPage';
import { Image, Linking, Modal, Platform, StyleSheet, ScrollView, View, Pressable, Share, useWindowDimensions } from "react-native";
import { FindifyTopBarOverlay } from "./components/FindifyTopBarOverlay";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { BasePage } from "./helpers/BasePage";
import { PlaceColors } from "./helpers/PlaceColors";
import { FindifyDataContext } from "@/contexts/FindifyDataContext";
import { FindifySimpleHeader, SimpleHeaderAction } from "./components/FindifySimpleHeader";
import { FindifyHTMLRender } from "./helpers/FindifyHTMLRender";
import { PlaceText } from "./components/PlaceText";
import { useTranslation } from "react-i18next";
import { FindifyFooterLogo, BottomNavigationPadding } from "./components/FindifyNavigation";
import { FindifyLoadingScreen } from "./components/FindifyLoadingScreen";
import { IconShadow } from "./helpers/DefaultShadows";

import { MakeContentDetailsContentVars, MakeContentDetailsSetActions, PlaceContentDetailsInitialUseEffect, getContentIdForModule, getFeaturedImageUrl, makeDateString, makeContentTitle } from "@/layouts/genericPages/helpers/DetailsHelpers";
import { ContentDetailsGallery } from "@/layouts/genericPages/components/ContentDetailsGallery";
import RNMapbox, { StyleURL } from "@rnmapbox/maps";
import Icon from "react-native-vector-icons/Ionicons";
import { getTranslatedString } from "./helpers/FindifyTranslatedString";
import { getFileUrl } from "@/config/environment";
import { getApiFromModule } from "./helpers/GetContentsByModule";
import { FindifyAppHeader } from "./components/FindifyAppHeader";
import { useSeoTitle } from "./helpers/SeoTitle";
import { getContentAudioUrl } from "./helpers/MediaHelpers";
import { SimpleAudioPlayer } from "./components/SimpleAudioPlayer";
import { FindifyMenuContext } from "@/contexts/FindifyMenuContext";

export const PlaceContentDetailsTab = ({ navigation, ...props }: any) => {
    const [contentModule, setContentModule] = useState<any>();
    const [contentSlug, setContentSlug] = useState<any>();
    const [hideDate, setHideDate] = useState(false);
    const { t } = useTranslation();
    const [content, setContent] = useState<any>();
    const findifyDataContext = useContext(FindifyDataContext);

    useEffect(() => {
        PlaceContentDetailsInitialUseEffect(props, findifyDataContext, setContent, setContentSlug, setContentModule, setHideDate);
    }, [])


    return <BasePage navigation={navigation}>
        <FindifyTopBarOverlay />
        {content && makeContentDetails(MakeContentDetailsPage, content, t, hideDate, contentModule, navigation, findifyDataContext)}
        {!content && <FindifyLoadingScreen />}
    </BasePage>;
};


export const makeContentDetails = (MakeContentDetailsPage: JSX.ElementType, content: any, t: any, hideDate: boolean, module: string, navigation: any, findifyDataContext: any, isLogged?: boolean) => {
    const {
        image,
        title,
        resume,
        description,
        dateString,
        rightActions,
        bottomButtons,
    } = MakeContentDetailsContentVars(content, module, findifyDataContext, t, navigation);

    return <MakeContentDetailsPage
        image={image}
        title={title}
        resume={resume}
        description={description}
        dateString={dateString}
        hideDate={hideDate}
        navigation={navigation}
        rightActions={rightActions}
        content={content}
        module={module}
        findifyDataContext={findifyDataContext}
        t={t}
        bottomButtons={bottomButtons}
        isLogged={isLogged}
    />
}



type ContentDetailsType = {
    image: string,
    title: string,
    resume: string,
    description: string,
    dateString: string,
    hideDate: boolean,
    navigation: any,
    rightActions: JSX.Element,
    bottomButtons: JSX.Element,
    content: any,
    findifyDataContext: any,
    module: string,
    t: any,
}
export const MakeContentDetailsPage = ({ image, title, navigation, resume, description, dateString, rightActions, content, t, hideDate, module, findifyDataContext, bottomButtons }: ContentDetailsType) => {
    const topTextStyle = {
        marginTop: 5,
        fontSize: 14,
        color: PlaceColors(findifyDataContext).text.withAlpha(160)
    };
    const [actionsList, setActionsList] = useState<SimpleHeaderAction[]>([]);
    const [textSizeModifier, setTextSizeModifier] = useState<number>(0);
    const findifyMenuContext = useContext(FindifyMenuContext);
    const videoUrl = getContentVideoUrl(content);
    const audioUrl = getContentAudioUrl(content);
    const isRmcWorkspace = Number(findifyDataContext?.workspace?.workspace_id) === 193;
    useEffect(() => {
        if (isRmcWorkspace && module === 'points_of_interest' && content?.point_of_interest_id) {
            recordRoomVisit(content).catch(error => console.error('Failed to save visit', error));
        }
    }, [content?.point_of_interest_id, module, isRmcWorkspace]);
    useSeoTitle(title, 'Museu da Terra de Miranda');

    useEffect(() => {
        MakeContentDetailsSetActions(setActionsList, title, findifyDataContext, content, module);
    }, [module])

    useEffect(() => {
        if (!isRmcWorkspace) return;
        const explicitMuseumSlug = String(content?.__museum_slug ?? '').trim().toLocaleLowerCase();
        if (module !== 'contents' && !explicitMuseumSlug) return;
        if (module === 'contents' && !isMuseumContent(content)) return;
        const slug = explicitMuseumSlug || String(content?.page_slug ?? '').trim().toLocaleLowerCase();
        if (!slug) return;
        findifyMenuContext?.setActiveMuseumSlug(slug);
        findifyMenuContext?.setActiveMuseumTitle(title);
    }, [content?.content_id, content?.page_slug, isRmcWorkspace, module, title]);

    if (content?.__loadError) {
        return <View style={[styles.errorContainer, { backgroundColor: PlaceColors(findifyDataContext).background.get() }]}>
            <PlaceText style={[styles.errorTitle, { color: PlaceColors(findifyDataContext).primary.get() }]}>
                {t('base.error') || 'Error'}
            </PlaceText>
            <PlaceText style={[styles.errorText, { color: PlaceColors(findifyDataContext).text.get() }]}>
                Não foi possível carregar este conteúdo.
            </PlaceText>
        </View>
    }

    if (module === 'contents' && isRmcWorkspace && isReviewContent(content)) {
        return <FindifyReviewDetails
            content={content}
            navigation={navigation}
            findifyDataContext={findifyDataContext}
            bottomButtons={bottomButtons}
        />
    }

    if (module === 'contents' && isRmcWorkspace) {
        return <FindifyGenericContentDetails
            content={content}
            image={image}
            title={title}
            resume={resume}
            description={description}
            navigation={navigation}
            findifyDataContext={findifyDataContext}
            videoUrl={videoUrl}
            audioUrl={audioUrl}
            bottomButtons={bottomButtons}
            t={t}
        />
    }

    if (module === 'points_of_interest' && isRmcWorkspace) {
        return <FindifyPoiDetails
            content={content}
            image={image}
            navigation={navigation}
            findifyDataContext={findifyDataContext}
            videoUrl={videoUrl}
            audioUrl={audioUrl}
            bottomButtons={bottomButtons}
        />
    }

    if (module === 'events' && isRmcWorkspace) {
        return <FindifyEventDetails content={content} image={image} title={title} description={description} dateString={dateString} navigation={navigation} findifyDataContext={findifyDataContext} t={t} bottomButtons={bottomButtons} />;
    }

    return <>
        <ScrollView
            bounces={false}
            showsHorizontalScrollIndicator={false}
            showsVerticalScrollIndicator={false}
            style={[styles.container, { backgroundColor: PlaceColors(findifyDataContext).background.get() }]}>
            <FindifySimpleHeader
                navigation={navigation}
                title={title}
                actions={actionsList}
                image={image} />
            <View style={[{
                backgroundColor: PlaceColors(findifyDataContext).background.get(),
                borderTopLeftRadius: 24,
                borderTopRightRadius: 24,
                marginTop: -24,
                padding: 20,
                paddingBottom: 20 + BottomNavigationPadding,
            }]}>
                <View style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 10,
                }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                        {!hideDate &&
                            <PlaceText style={{
                                fontSize: 14,
                                color: PlaceColors(findifyDataContext).text.withAlpha(200),
                                fontWeight: '500',
                            }}>{dateString}</PlaceText>
                        }
                    </View>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: PlaceColors(findifyDataContext).primary.withAlpha(20), borderRadius: 20, paddingHorizontal: 8, paddingVertical: 4 }}>
                            <Pressable onPress={() => setTextSizeModifier(prev => Math.max(prev - 2, -4))} style={{ paddingHorizontal: 8 }}>
                                <PlaceText style={{ fontSize: 16, fontWeight: 'bold', color: PlaceColors(findifyDataContext).primary.get() }}>A-</PlaceText>
                            </Pressable>
                            <View style={{ width: 1, height: 16, backgroundColor: PlaceColors(findifyDataContext).primary.withAlpha(50) }} />
                            <Pressable onPress={() => setTextSizeModifier(prev => Math.min(prev + 2, 8))} style={{ paddingHorizontal: 8 }}>
                                <PlaceText style={{ fontSize: 18, fontWeight: 'bold', color: PlaceColors(findifyDataContext).primary.get() }}>A+</PlaceText>
                            </Pressable>
                        </View>
                        {rightActions}
                    </View>
                </View>
                
                {/* address / resume */}
                {!!resume && <PlaceText style={[topTextStyle, { fontSize: 15 + (textSizeModifier * 0.5), marginBottom: 15, fontWeight: '600', color: PlaceColors(findifyDataContext).text.get() }]}>{resume}</PlaceText>}

                {videoUrl && <ContentVideoPlayer videoUrl={videoUrl} />}

                <FindifyHTMLRender
                    baseStyles={{
                        color: PlaceColors(findifyDataContext).text.get(),
                        fontSize: 16 + textSizeModifier,
                        lineHeight: (16 + textSizeModifier) * 1.5,
                    }}
                    htmlCode={description} />

                {module === 'routes' && <RouteDetailsMap content={content} findifyDataContext={findifyDataContext} navigation={navigation} />}

                {content.galleries && <View style={{ marginTop: 20 }}><ContentDetailsGallery findifyDataContext={findifyDataContext} t={t} content={content} navigation={navigation} /></View>}
                <FindifyFooterLogo findifyDataContext={findifyDataContext} />
            </View >
        </ScrollView>
        {bottomButtons}
    </>
}

const FindifyEventDetails = ({ content, image, title, description, dateString, navigation, findifyDataContext, t, bottomButtons }: any) => {
    const colors = PlaceColors(findifyDataContext);
    const addToCalendar = async () => {
        const start = new Date(content.start_date).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
        const end = new Date(content.end_date ?? content.start_date).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
        const ics = `BEGIN:VCALENDAR\nVERSION:2.0\nBEGIN:VEVENT\nUID:findify-event-${content.event_id}@findify.cloud\nDTSTAMP:${start}\nDTSTART:${start}\nDTEND:${end}\nSUMMARY:${title}\nDESCRIPTION:${String(content.resume?.['pt-PT'] ?? '').replace(/\n/g, ' ')}\nEND:VEVENT\nEND:VCALENDAR`;
        if (Platform.OS === 'web') {
            const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a'); link.href = url; link.download = `${content.page_slug ?? 'evento'}.ics`; link.click(); URL.revokeObjectURL(url);
        } else {
            await Share.share({ title, message: ics });
        }
    };
    const shareEvent = async () => {
        const domain = findifyDataContext?.workspace?.primary_domain?.domain_name ?? 'links.findify.cloud';
        const url = `https://${domain}/dynamicLink?workspace_id=${findifyDataContext?.workspace?.workspace_id}&type=details&module=events&content_id=${content.event_id}`;
        const message = `${title}\n${dateString}\n${url}`;
        if (Platform.OS === 'web' && typeof navigator !== 'undefined' && navigator.share) {
            await navigator.share({ title, text: `${title}\n${dateString}`, url });
        } else {
            await Share.share({ title, message });
        }
    };
    return <ScrollView style={[styles.container, { backgroundColor: colors.background.get() }]} contentContainerStyle={{ paddingBottom: BottomNavigationPadding + 24 }}>
        <View style={eventDetailStyles.hero}>{image ? <Image source={{ uri: image }} style={StyleSheet.absoluteFillObject} resizeMode="cover" /> : null}<View style={eventDetailStyles.shade} /><View style={eventDetailStyles.top}><Pressable onPress={() => navigation.goBack()}><Icon name="chevron-back" size={30} color="#fff" /></Pressable><View style={{ flexDirection: 'row', gap: 10 }}><Pressable onPress={shareEvent} style={eventDetailStyles.icon}><Icon name="share-social-outline" size={22} color={colors.primary.get()} /></Pressable><Pressable onPress={addToCalendar} style={eventDetailStyles.icon}><Icon name="calendar-outline" size={22} color={colors.primary.get()} /></Pressable></View></View><View style={eventDetailStyles.heroText}><PlaceText style={eventDetailStyles.eyebrow}>{findifyDataContext?.language?.startsWith('en') ? 'EVENT' : 'EVENTO'}</PlaceText><PlaceText style={eventDetailStyles.title}>{title}</PlaceText></View></View>
        <View style={[eventDetailStyles.body, { backgroundColor: colors.background.get() }]}><View style={eventDetailStyles.dateCard}><Icon name="calendar-outline" size={24} color={colors.secondary.get()} /><View style={{ flex: 1 }}><PlaceText style={[eventDetailStyles.dateMain, { color: colors.primary.get() }]}>{dateString}</PlaceText><PlaceText style={eventDetailStyles.dateHint}>{findifyDataContext?.language?.startsWith('en') ? 'Event dates' : 'Datas do evento'}</PlaceText></View><Pressable onPress={addToCalendar} style={[eventDetailStyles.button, { backgroundColor: colors.primary.get() }]}><PlaceText style={eventDetailStyles.buttonText}>{findifyDataContext?.language?.startsWith('en') ? 'Add' : 'Adicionar'}</PlaceText></Pressable></View>{!!content.place_address && <PlaceText style={eventDetailStyles.address}>{content.place_address}</PlaceText>}{!!content.resume && <PlaceText style={eventDetailStyles.resume}>{getTranslatedString(content.resume, findifyDataContext)}</PlaceText>}<FindifyHTMLRender baseStyles={{ color: colors.text.get(), fontSize: 16, lineHeight: 24 }} htmlCode={description} /></View></ScrollView>;
};

const eventDetailStyles = StyleSheet.create({
    hero: { height: 360, width: '100%', backgroundColor: '#021E49', position: 'relative', overflow: 'hidden' }, shade: { ...StyleSheet.absoluteFillObject, backgroundColor: 'rgba(2,30,73,.55)' }, top: { position: 'absolute', top: 24, left: 16, right: 16, flexDirection: 'row', justifyContent: 'space-between' }, icon: { backgroundColor: '#fff', borderRadius: 24, padding: 12 }, heroText: { position: 'absolute', bottom: 28, left: 20, right: 20, gap: 8 }, eyebrow: { color: '#E7C58F', fontSize: 11, fontWeight: '900', letterSpacing: 1.3 }, title: { color: '#fff', fontSize: 30, lineHeight: 35, fontWeight: '800' }, body: { marginTop: -20, borderTopLeftRadius: 24, borderTopRightRadius: 24, padding: 20, paddingTop: 24, minHeight: 300 }, dateCard: { flexDirection: 'row', alignItems: 'center', gap: 12, borderRadius: 16, padding: 14, backgroundColor: 'rgba(188,149,94,.14)', marginBottom: 18 }, dateMain: { fontSize: 13, fontWeight: '800' }, dateHint: { color: '#4FA5A1', fontSize: 11, marginTop: 3 }, button: { marginLeft: 'auto', borderRadius: 10, paddingHorizontal: 12, paddingVertical: 10 }, buttonText: { color: '#fff', fontSize: 12, fontWeight: '800' }, address: { color: '#4FA5A1', fontSize: 14, fontWeight: '700', marginBottom: 14 }, resume: { color: '#021E49', fontSize: 16, lineHeight: 23, fontWeight: '700', marginBottom: 16 },
});

const getContentVideoUrl = (content: any) => {
    const mediaPath = content?.file_video_media?.file_path
        || content?.video_media?.file_path
        || content?.file_video?.file_path;

    if (mediaPath) return getFileUrl() + mediaPath;
    if (typeof content?.file_video === 'string' && content.file_video.startsWith('/')) return getFileUrl() + content.file_video;
    if (typeof content?.file_video === 'string' && content.file_video.startsWith('http')) return content.file_video;

    // The public POI endpoint currently returns only the media id for this file.
    if (+content?.file_video === 5080) return getFileUrl() + '/155/mb_salao_nobre.mp4';

    return undefined;
}

const ContentVideoPlayer = ({ videoUrl }: { videoUrl: string }) => {
    if (Platform.OS === 'web') {
        return <View style={styles.videoContainer}>
            {React.createElement('video', {
                src: videoUrl,
                controls: true,
                playsInline: true,
                style: {
                    width: '100%',
                    height: '100%',
                    borderRadius: 14,
                    backgroundColor: '#000',
                    objectFit: 'contain',
                },
            })}
        </View>
    }

    return null;
}

const stripHtml = (html: string) => {
    return (html ?? '')
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/p>/gi, '\n')
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/\s+\n/g, '\n')
        .replace(/\n\s+/g, '\n')
        .trim();
}

const titleCase = (text: string) => {
    return (text ?? '').toLocaleLowerCase('pt-PT').replace(/(^|\s|\/)(\p{L})/gu, (match) => match.toLocaleUpperCase('pt-PT'));
}

const getPoiTitle = (content: any, language: string) => {
    return titleCase(content?.title?.[language] ?? content?.title?.['en-US'] ?? content?.title?.['pt-PT'] ?? '');
}

const getPoiDescription = (content: any, language: string) => {
    return stripHtml(content?.description?.[language] ?? '');
}

const isEnglishLanguage = (language?: string) => language?.startsWith('en');
const localizedText = (findifyDataContext: any, pt: string, en: string) => {
    return isEnglishLanguage(findifyDataContext?.language) ? en : pt;
};

const FLOOR_SLUG_REGEX = /^(?:(.+)-)?floor-(\d+)(-accessible)?$/i;
const getRouteSpaceSlugFromContent = (content: any) => {
    return content?.routes?.[0]?.route?.page_slug
        || content?.routes?.[0]?.route?.slug
        || content?.route?.page_slug
        || content?.route?.slug
        || '';
}

const getFloorRouteMeta = (route: any) => {
    const slug = String(route?.page_slug ?? route?.slug ?? '').trim().toLowerCase();
    const match = slug.match(FLOOR_SLUG_REGEX);
    if (!match) return null;

    return {
        spaceSlug: match[1] || '',
        order: Number(match[2]),
        accessible: !!match[3],
    };
}

const getTranslatedValues = (value: any) => {
    if (!value) return [];
    if (typeof value === 'string') return [value];
    if (typeof value === 'object') return Object.values(value).filter(Boolean).map((line: any) => String(line));
    return [];
}

const findRoomObjectsCategory = (categories: any[], roomSlug?: string) => {
    if (!roomSlug) return null;
    const expectedName = `#${roomSlug}`.toLocaleLowerCase();
    return categories.find((category: any) => {
        const names = getTranslatedValues(category?.name).map((line: string) => line.trim().toLocaleLowerCase());
        const pageSlug = String(category?.page_slug ?? '').trim().toLocaleLowerCase();
        return names.includes(expectedName)
            || pageSlug === expectedName
            || pageSlug === roomSlug.toLocaleLowerCase();
    }) ?? null;
}

const isReviewContent = (content: any) => {
    return content?.categories?.some((line: any) => line?.category?.page_slug === 'review');
}

const normalizeSlug = (value: any) => String(value ?? '').trim().toLocaleLowerCase();

const isMuseumContent = (content: any) => {
    return content?.categories?.some((line: any) => {
        const category = line?.category ?? line;
        const slugs = [
            category?.page_slug,
            category?.slug,
            category?.code,
            line?.page_slug,
            line?.slug,
        ].map(normalizeSlug);
        const names = getTranslatedValues(category?.name ?? line?.name)
            .map((value: string) => normalizeSlug(value));
        return slugs.includes('museus') || names.includes('museus');
    });
}

const getReviewFormId = (content: any) => {
    const description = Object.values(content?.description ?? {}).join(' ');
    const match = description.match(/data-lexical-form-id=["'](\d+)["']/);
    return match?.[1];
}

const ratingIcons = [
    'sad-outline',
    'sad',
    'remove-circle-outline',
    'happy-outline',
    'happy',
];

const FindifyReviewDetails = ({ content, navigation, findifyDataContext, bottomButtons }: any) => {
    const { width, height } = useWindowDimensions();
    const isTablet = width >= 768;
    const isLandscapeTablet = isTablet && width > height;
    const colors = PlaceColors(findifyDataContext);
    const [formData, setFormData] = useState<any>(null);
    const [ratings, setRatings] = useState<Record<string, number>>({});
    const [submitted, setSubmitted] = useState(false);
    const formId = getReviewFormId(content);
    const selectedLanguage = findifyDataContext?.language ?? 'pt-PT';
    useEffect(() => {
        if (!formId) return;
        const formsApi = getApiFromModule('forms', findifyDataContext);
        formsApi?.get(formId, null).then((form: any) => {
            form?.questions?.sort((qA: any, qB: any) => Number(qA.sequence ?? 0) - Number(qB.sequence ?? 0));
            setFormData(form);
        }).catch((error: any) => {
            console.error('Failed to load review form:', error);
        });
    }, [formId, findifyDataContext?.workspace?.workspace_id]);

    const submitReview = () => {
        if (!formData) return;
        const formsApi = getApiFromModule('forms', findifyDataContext);
        const questionResponses = (formData.questions ?? [])
            .filter((question: any) => ratings[question.form_question_id] !== undefined)
            .map((question: any) => ({
                form_question_id: question.form_question_id,
                language: findifyDataContext.language,
                response: ratings[question.form_question_id],
                choices: [],
            }));

        formsApi?.create({
            form_id: formId,
            language: findifyDataContext.language,
            question_responses: questionResponses,
        }, null).then(() => {
            setSubmitted(true);
        });
    }

    return <>
        <ScrollView
            bounces={false}
            showsHorizontalScrollIndicator={false}
            showsVerticalScrollIndicator={false}
            style={[styles.reviewPage, { backgroundColor: colors.background.get() }]}>
            <FindifyAppHeader findifyDataContext={findifyDataContext} navigation={navigation} />
            <View style={[
                styles.reviewContent,
                isTablet ? styles.reviewContentTablet : null,
                isLandscapeTablet ? styles.reviewContentLandscapeTablet : null,
            ]}>
                <View style={[styles.reviewTitleBlock, { borderLeftColor: colors.secondary.get() }]}>
                    <PlaceText style={[styles.reviewTitle, { color: colors.primary.get() }]}>{localizedText(findifyDataContext, 'Avaliação', 'Review', 'Évaluation')}</PlaceText>
                    <PlaceText style={[styles.reviewLead, { color: colors.text.get() }]}>{localizedText(findifyDataContext, 'A sua opinião é valiosa para nós.', 'Your feedback is valuable to us.', 'Votre avis est précieux pour nous.')}</PlaceText>
                </View>

                {(formData?.questions ?? []).map((question: any, index: number) => {
                    const selectedValue = ratings[question.form_question_id];
                    const questionText = question.question?.[selectedLanguage]
                        ?? (isEnglishLanguage(selectedLanguage) ? question.question?.['en-US'] ?? question.question?.['en-GB'] : question.question?.['pt-PT'])
                        ?? getTranslatedString(question.question, findifyDataContext);
                    return <View key={question.form_question_id} style={[styles.reviewQuestionCard, { borderColor: colors.secondary.withAlpha(75), backgroundColor: colors.background.withAlpha(170) }]}>
                        <PlaceText style={[styles.reviewQuestionTitle, { color: colors.primary.get() }]}>{index + 1}. {questionText}</PlaceText>
                        <View style={styles.reviewRatingRow}>
                            {ratingIcons.map((iconName, ratingIndex) => {
                                const value = ratingIndex + 1;
                                const selected = selectedValue === value;
                                return <Pressable
                                    key={iconName + value}
                                    style={styles.reviewRatingButton}
                                    onPress={() => setRatings((current) => ({
                                        ...current,
                                        [question.form_question_id]: value,
                                    }))}>
                                    <Icon
                                        name={iconName}
                                        size={36}
                                        color={selected ? colors.secondary.get() : colors.primary.withAlpha(150)}
                                        style={{ opacity: selected ? 1 : 0.82 }}
                                    />
                                </Pressable>
                            })}
                        </View>
                    </View>
                })}

                <Pressable style={[styles.reviewSubmitButton, { backgroundColor: colors.primary.get() }]} onPress={submitReview}>
                    <PlaceText style={styles.reviewSubmitText}>{submitted ? localizedText(findifyDataContext, 'AVALIAÇÃO SUBMETIDA', 'REVIEW SUBMITTED', 'ÉVALUATION ENVOYÉE') : localizedText(findifyDataContext, 'SUBMETER AVALIAÇÃO', 'SUBMIT REVIEW', "ENVOYER L'ÉVALUATION")}</PlaceText>
                </Pressable>

                {findifyDataContext?.workspace?.app_settings?.app_banner_url ? (
                    <Image
                        source={{ uri: findifyDataContext.workspace.app_settings.app_banner_url }}
                        resizeMode="cover"
                        style={styles.reviewBottomImage}
                    />
                ) : null}
                <FindifyFooterLogo findifyDataContext={findifyDataContext} />
            </View>
        </ScrollView>
        {bottomButtons}
    </>
}

const FindifyGenericContentDetails = ({
    content,
    image,
    title,
    resume,
    description,
    navigation,
    findifyDataContext,
    videoUrl,
    audioUrl,
    bottomButtons,
    t,
}: any) => {
    const colors = PlaceColors(findifyDataContext);
    const { width, height } = useWindowDimensions();
    const isTablet = width >= 768;
    const isLandscapeTablet = isTablet && width > height;
    const displayTitle = titleCase(title || getTranslatedString(content?.title ?? content?.name, findifyDataContext) || '');
    const cleanResume = stripHtml(resume);

    return <>
        <ScrollView
            bounces={false}
            showsHorizontalScrollIndicator={false}
            showsVerticalScrollIndicator={false}
            style={[styles.findifyPoiPage, { backgroundColor: colors.background.get() }]}>
            <FindifyAppHeader findifyDataContext={findifyDataContext} navigation={navigation} />

            {image ? (
                <View style={[
                    styles.findifyPoiHero,
                    isTablet ? styles.findifyPoiHeroTablet : null,
                    isLandscapeTablet ? styles.findifyPoiHeroLandscapeTablet : null,
                ]}>
                    <Image source={{ uri: image }} resizeMode="cover" style={styles.findifyPoiHeroImage} />
                    {videoUrl ? (
                        <View style={[styles.findifyHeroPlayHint, { backgroundColor: colors.secondary.get(), borderColor: colors.background.get() }]}>
                            <Icon name="play" size={42} color={colors.primary.get()} />
                        </View>
                    ) : null}
                    <View style={[styles.findifyPoiHeroCaption, { backgroundColor: colors.primary.withAlpha(195) }]}>
                        <PlaceText style={[styles.findifyPoiHighlightLabel, { color: colors.secondary.get() }]}>{localizedText(findifyDataContext, 'Museu da Terra de Miranda', 'Museu da Terra de Miranda')}</PlaceText>
                        <PlaceText style={styles.findifyPoiHighlightTitle}>{displayTitle}</PlaceText>
                    </View>
                </View>
            ) : null}

            <View style={[
                styles.findifyPoiContent,
                isTablet ? styles.findifyPoiContentTablet : null,
                isLandscapeTablet ? styles.findifyPoiContentLandscapeTablet : null,
            ]}>
                {!!cleanResume && <View style={[styles.findifyPoiIntroCard, { borderLeftColor: colors.secondary.get(), backgroundColor: colors.background.solidAlphaBlackBase(18) }]}>
                    <PlaceText style={[styles.findifyPoiSubtitle, { color: colors.text.get() }]}>{cleanResume}</PlaceText>
                </View>}

                {videoUrl ? <ContentVideoPlayer videoUrl={videoUrl} /> : null}

                <SimpleAudioPlayer audioUrl={audioUrl} findifyDataContext={findifyDataContext} title={localizedText(findifyDataContext, 'Audioguia', 'Audio Guide', 'Audioguide')} />

                {!!description && <View style={styles.findifyPoiLanguageSection}>
                    <View style={styles.findifyPoiLanguageTitleRow}>
                        <Icon name="language-outline" size={18} color={colors.secondary.get()} />
                        <PlaceText style={[styles.findifyPoiLanguageTitle, { color: colors.primary.get() }]}>
                            {findifyDataContext?.language === 'mwl-PT' ? 'MIRANDÊS' : localizedText(findifyDataContext, 'PORTUGUÊS', 'ENGLISH')}
                        </PlaceText>
                    </View>
                    <FindifyHTMLRender
                        baseStyles={{
                            color: colors.text.get(),
                            fontSize: 17,
                            lineHeight: 27,
                        }}
                        htmlCode={description}
                    />
                </View>}

                {content.galleries ? (
                    <View style={styles.findifyDetailsGallery}>
                        <ContentDetailsGallery findifyDataContext={findifyDataContext} t={t} content={content} navigation={navigation} />
                    </View>
                ) : null}
                <FindifyFooterLogo findifyDataContext={findifyDataContext} />
            </View>
        </ScrollView>
        {bottomButtons}
    </>
}

const FindifyPoiDetails = ({ content, image, navigation, findifyDataContext, videoUrl, audioUrl, bottomButtons }: any) => {
    const findifyMenuContext = useContext(FindifyMenuContext);
    const colors = PlaceColors(findifyDataContext);
    const { width, height } = useWindowDimensions();
    const isTablet = width >= 768;
    const isLandscapeTablet = isTablet && width > height;
    const [videoOpen, setVideoOpen] = useState(false);
    const [roomInfoOpen, setRoomInfoOpen] = useState(false);
    const [nextPoint, setNextPoint] = useState<any>(null);
    const [roomObjects, setRoomObjects] = useState<any[]>([]);
    const [hasRoomObjectsSection, setHasRoomObjectsSection] = useState(false);
    const title = titleCase(getTranslatedString(content?.title ?? content?.name, findifyDataContext) || '');
    const description = stripHtml(getTranslatedString(content?.description, findifyDataContext));
    useEffect(() => {
        if (content?.__guidedVisit) return;
        const api = getApiFromModule('routes', findifyDataContext);
        const selectedAccessible = Number(findifyDataContext?.workspace?.workspace_id) === 193
            ? false
            : findifyDataContext?.visitVersion === 'accessible';
        const currentRouteMeta = getFloorRouteMeta({ page_slug: getRouteSpaceSlugFromContent(content) });
        const activeSpaceSlug = currentRouteMeta?.spaceSlug || findifyMenuContext?.activeMuseumSlug || '';
        api?.getList(0, 100, null, {}, null)
            .then((res: any) => {
                const routeSummaries = (res?.results ?? [])
                    .map((route: any) => ({ ...route, __floorMeta: getFloorRouteMeta(route) }))
                    .filter((route: any) => route.__floorMeta && route.__floorMeta.accessible === selectedAccessible)
                    .filter((route: any) => !activeSpaceSlug || route.__floorMeta.spaceSlug === activeSpaceSlug)
                    .sort((a: any, b: any) => a.__floorMeta.order - b.__floorMeta.order);

                return Promise.all(routeSummaries.map((route: any) => {
                    const routeLookup = route?.route_id ?? route?.page_slug;
                    return api?.get(routeLookup, null, null)
                        .then((details: any) => ({ ...details, __floorMeta: route.__floorMeta }))
                        .catch(() => null);
                }));
            })
            .then((routes: any[]) => {
                const routePoints = routes
                    .filter(Boolean)
                    .flatMap((route: any) => (route?.points_of_interest ?? [])
                        .slice()
                        .sort((a: any, b: any) => Number(a.sequence ?? 0) - Number(b.sequence ?? 0)));
                const currentIndex = routePoints.findIndex((routePoint: any) => routePoint?.point_of_interest?.point_of_interest_id === content?.point_of_interest_id);
                setNextPoint(currentIndex >= 0 ? routePoints[currentIndex + 1]?.point_of_interest ?? null : null);
            })
            .catch(() => setNextPoint(null));
    }, [content?.point_of_interest_id, findifyDataContext?.workspace?.workspace_id, findifyDataContext?.visitVersion, findifyMenuContext?.activeMuseumSlug]);

    useEffect(() => {
        const roomSlug = content?.page_slug;
        if (!roomSlug) {
            setRoomObjects([]);
            setHasRoomObjectsSection(false);
            return;
        }

        const categoriesApi = getApiFromModule('categories', findifyDataContext);
        const pointsApi = getApiFromModule('points_of_interest', findifyDataContext);

        categoriesApi?.getList(0, 250, null, { category_type: 'points_of_interest', active: 1 }, null)
            .then((categoriesData: any) => {
                const roomCategory = findRoomObjectsCategory(categoriesData?.results ?? [], roomSlug);
                if (!roomCategory?.category_id) {
                    setRoomObjects([]);
                    setHasRoomObjectsSection(false);
                    return null;
                }

                setHasRoomObjectsSection(true);
                return pointsApi?.getList(0, 50, null, {
                    'categories.category.category_id_in': JSON.stringify([roomCategory.category_id])
                }, null);
            })
            .then((pointsData: any) => {
                if (!pointsData?.results) return;
                const objects = pointsData.results.filter((item: any) => item?.point_of_interest_id !== content?.point_of_interest_id);
                setRoomObjects(objects);
            })
            .catch((error: any) => {
                console.error('Failed to load room featured objects:', error);
                setRoomObjects([]);
                setHasRoomObjectsSection(false);
            });
    }, [content?.page_slug, content?.point_of_interest_id, findifyDataContext?.workspace?.workspace_id]);

    const openPoint = (point: any) => {
        if (!point?.point_of_interest_id) return;
        navigation.push("PlaceContentDetails", {
            module: "points_of_interest",
            content_id: point.point_of_interest_id,
            page_slug: point.page_slug,
        });
    }

    return <>
        <ScrollView
            bounces={false}
            showsHorizontalScrollIndicator={false}
            showsVerticalScrollIndicator={false}
            style={[styles.findifyPoiPage, { backgroundColor: colors.background.get() }]}>
            <FindifyAppHeader findifyDataContext={findifyDataContext} navigation={navigation} />

            <View style={[
                styles.findifyPoiHero,
                isTablet ? styles.findifyPoiHeroTablet : null,
                isLandscapeTablet ? styles.findifyPoiHeroLandscapeTablet : null,
            ]}>
                {image ? <Image source={{ uri: image }} resizeMode="cover" style={styles.findifyPoiHeroImage} /> : null}
                {videoUrl ? (
                    <Pressable style={[styles.findifyPoiPlayButton, { backgroundColor: colors.secondary.get(), borderColor: colors.background.get() }]} onPress={() => setVideoOpen(true)}>
                        <Icon name="play" size={42} color={colors.primary.get()} />
                    </Pressable>
                ) : null}
                <View style={[styles.findifyPoiHeroCaption, { backgroundColor: colors.primary.withAlpha(195) }]}>
                    <PlaceText style={[styles.findifyPoiHighlightLabel, { color: colors.secondary.get() }]}>{localizedText(findifyDataContext, 'Destaque', 'Highlight')}</PlaceText>
                    <PlaceText style={styles.findifyPoiHighlightTitle}>{title}</PlaceText>
                </View>
            </View>

            <View style={[
                styles.findifyPoiContent,
                isTablet ? styles.findifyPoiContentTablet : null,
                isLandscapeTablet ? styles.findifyPoiContentLandscapeTablet : null,
            ]}>
                <SimpleAudioPlayer audioUrl={audioUrl} findifyDataContext={findifyDataContext} title={localizedText(findifyDataContext, 'Audioguia', 'Audio Guide', 'Audioguide')} />

                {hasRoomObjectsSection && !!description ? (
                    <Pressable style={[styles.roomInfoLinkCard, { borderColor: colors.primary.withAlpha(32), backgroundColor: colors.background.solidAlphaBlackBase(14) }]} onPress={() => setRoomInfoOpen(true)}>
                        <View style={[styles.roomInfoLinkIcon, { backgroundColor: colors.primary.get() }]}>
                            <Icon name="information-circle-outline" size={26} color="#fff" />
                        </View>
                        <PlaceText style={[styles.roomInfoLinkText, { color: colors.primary.get() }]}>{localizedText(findifyDataContext, 'Informações da sala', 'Room information')}</PlaceText>
                        <Icon name="chevron-forward-outline" size={24} color={colors.primary.get()} />
                    </Pressable>
                ) : null}

                {!stripHtml(description) && !hasRoomObjectsSection ? <PlaceText style={{ color: colors.text.get(), fontSize: 16, lineHeight: 24, marginVertical: 20 }}>{localizedText(findifyDataContext, 'A descrição deste espaço ainda não está disponível.', 'The description of this space is not yet available.')}</PlaceText> : null}

                {!!description && !hasRoomObjectsSection ? <View style={styles.findifyPoiLanguageSection}>
                    <View style={styles.findifyPoiLanguageTitleRow}>
                        <Icon name="globe-outline" size={18} color={colors.secondary.get()} />
                        <PlaceText style={[styles.findifyPoiLanguageTitle, { color: colors.primary.get() }]}>{findifyDataContext?.language === 'mwl-PT' ? 'MIRANDÊS' : localizedText(findifyDataContext, 'PORTUGUÊS', 'ENGLISH')}</PlaceText>
                    </View>
                    <PlaceText style={[styles.findifyPoiParagraph, { color: colors.text.get() }]}>{description}</PlaceText>
                </View> : null}

                {roomObjects.length > 0 ? (
                    <View style={styles.featuredCollectionSection}>
                        <View style={styles.featuredCollectionTitleRow}>
                            <View style={[styles.featuredCollectionLine, { backgroundColor: colors.secondary.withAlpha(80) }]} />
                            <PlaceText style={[styles.featuredCollectionTitle, { color: colors.primary.get() }]}>{localizedText(findifyDataContext, 'Acervo em destaque', 'Featured collection')}</PlaceText>
                            <View style={[styles.featuredCollectionLine, { backgroundColor: colors.secondary.withAlpha(80) }]} />
                        </View>
                        {roomObjects.map((object: any) => {
                            const objectImage = getFeaturedImageUrl(object);
                            const objectTitle = makeContentTitle(object, 'points_of_interest', findifyDataContext);
                            return <Pressable
                                key={`room_object_${object.point_of_interest_id}`}
                                style={styles.featuredObjectRow}
                                onPress={() => openPoint(object)}>
                                <View style={styles.featuredObjectThumbWrap}>
                                    {objectImage ? <Image source={{ uri: objectImage }} resizeMode="cover" style={styles.featuredObjectThumb} /> : null}
                                </View>
                                <PlaceText numberOfLines={2} style={[styles.featuredObjectTitle, { color: colors.text.withAlpha(220) }]}>{objectTitle}</PlaceText>
                                <Icon name="chevron-forward-circle" size={32} color={colors.text.withAlpha(150)} />
                            </Pressable>
                        })}
                    </View>
                ) : null}

                {nextPoint ? (
                    <Pressable style={[styles.findifyPoiNextCard, { backgroundColor: colors.primary.get(), borderColor: colors.secondary.get() }]} onPress={() => openPoint(nextPoint)}>
                        <View style={{ flex: 1 }}>
                            <PlaceText style={styles.findifyPoiNextTitle}>{localizedText(findifyDataContext, 'Próxima Sala', 'Next Room')}</PlaceText>
                            <PlaceText style={styles.findifyPoiNextCopy}>
                                {localizedText(findifyDataContext, 'Sugestão: ', 'Suggested: ')}
                                <PlaceText style={styles.findifyPoiNextCopyStrong}>{titleCase(getTranslatedString(nextPoint?.title ?? nextPoint?.name, findifyDataContext))}</PlaceText>
                            </PlaceText>
                        </View>
                        <View style={[styles.findifyPoiNextIcon, { backgroundColor: colors.secondary.get() }]}>
                            <Icon name="arrow-forward-outline" size={34} color={colors.primary.get()} />
                        </View>
                    </Pressable>
                ) : null}
                <FindifyFooterLogo findifyDataContext={findifyDataContext} />
            </View>
        </ScrollView>

        <Modal visible={videoOpen} transparent={true} animationType="fade" onRequestClose={() => setVideoOpen(false)}>
            <View style={styles.findifyVideoModal}>
                <Pressable style={styles.findifyVideoClose} onPress={() => setVideoOpen(false)}>
                    <Icon name="close-outline" size={34} color="#fff" />
                </Pressable>
                {videoUrl ? <ContentVideoPlayer videoUrl={videoUrl} /> : null}
            </View>
        </Modal>
        <Modal visible={roomInfoOpen} transparent={false} animationType="slide" onRequestClose={() => setRoomInfoOpen(false)}>
            <View style={[styles.roomInfoModalPage, { backgroundColor: colors.background.get() }]}>
                <FindifyAppHeader findifyDataContext={findifyDataContext} navigation={navigation} showAccessibility={false} />
                <Pressable style={[styles.roomInfoModalClose, { backgroundColor: colors.primary.get() }]} onPress={() => setRoomInfoOpen(false)}>
                    <Icon name="close-outline" size={30} color="#fff" />
                </Pressable>
                <ScrollView
                    bounces={false}
                    showsVerticalScrollIndicator={false}
                    contentContainerStyle={[
                        styles.roomInfoModalContent,
                        isTablet ? styles.roomInfoModalContentTablet : null,
                    ]}>
                    <PlaceText style={[styles.roomInfoModalTitle, { color: colors.primary.get() }]}>{title}</PlaceText>
                    <View style={styles.findifyPoiLanguageSection}>
                        <View style={styles.findifyPoiLanguageTitleRow}>
                            <Icon name="globe-outline" size={18} color={colors.secondary.get()} />
                            <PlaceText style={[styles.findifyPoiLanguageTitle, { color: colors.primary.get() }]}>{findifyDataContext?.language === 'mwl-PT' ? 'MIRANDÊS' : localizedText(findifyDataContext, 'PORTUGUÊS', 'ENGLISH')}</PlaceText>
                        </View>
                        <PlaceText style={[styles.findifyPoiParagraph, { color: colors.text.get() }]}>{description}</PlaceText>
                    </View>
                </ScrollView>
            </View>
        </Modal>
        {bottomButtons}
    </>
}

const styles = StyleSheet.create({
    text: {
        padding: 10,
        paddingBottom: 10 + BottomNavigationPadding,
    },
    container: {
        height: '100%',
        width: '100%',
    },

    headerIcon: {
        padding: 5,
        ...IconShadow,
    },
    errorContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        padding: 24,
    },
    errorTitle: {
        fontSize: 24,
        fontWeight: 'bold',
        marginBottom: 8,
    },
    errorText: {
        fontSize: 16,
        textAlign: 'center',
    },
    videoContainer: {
        width: '100%',
        height: 240,
        marginBottom: 18,
        borderRadius: 14,
        overflow: 'hidden',
        backgroundColor: '#000',
    },
    findifyPoiPage: {
        flex: 1,
        backgroundColor: '#f9f7f2',
    },
    findifyPoiHeader: {
        height: 46,
        backgroundColor: '#f9f7f2',
        borderBottomWidth: 1,
        borderBottomColor: 'rgba(226, 177, 60, 0.45)',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 20,
    },
    findifyPoiHeaderButton: {
        width: 40,
        height: 40,
        alignItems: 'center',
        justifyContent: 'center',
    },
    findifyPoiLogo: {
        width: 118,
        height: 30,
    },
    findifyPoiHero: {
        aspectRatio: 4 / 5,
        width: '100%',
        backgroundColor: '#1f120f',
        position: 'relative',
        overflow: 'hidden',
    },
    findifyPoiHeroTablet: {
        maxWidth: 820,
        alignSelf: 'center',
        width: '100%',
    },
    findifyPoiHeroLandscapeTablet: {
        aspectRatio: 16 / 9,
        maxWidth: 1100,
    },
    findifyPoiHeroImage: {
        width: '100%',
        height: '100%',
        opacity: 0.92,
    },
    findifyPoiPlayButton: {
        position: 'absolute',
        left: '50%',
        top: '50%',
        marginLeft: -40,
        marginTop: -40,
        width: 80,
        height: 80,
        borderRadius: 40,
        borderWidth: 2,
        borderColor: '#f5c443',
        backgroundColor: 'rgba(245, 196, 67, 0.86)',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2,
        shadowColor: '#000',
        shadowOpacity: 0.32,
        shadowRadius: 16,
        shadowOffset: { width: 0, height: 8 },
    },
    findifyHeroPlayHint: {
        position: 'absolute',
        left: '50%',
        top: '50%',
        marginLeft: -40,
        marginTop: -40,
        width: 80,
        height: 80,
        borderRadius: 40,
        borderWidth: 2,
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2,
        shadowColor: '#000',
        shadowOpacity: 0.32,
        shadowRadius: 16,
        shadowOffset: { width: 0, height: 8 },
    },
    findifyPoiHeroCaption: {
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        paddingHorizontal: 24,
        paddingTop: 18,
        paddingBottom: 24,
        backgroundColor: 'rgba(153, 0, 0, 0.72)',
    },
    findifyPoiHighlightLabel: {
        color: '#f5c443',
        fontSize: 13,
        lineHeight: 18,
        fontWeight: '700',
        letterSpacing: 0.5,
    },
    findifyPoiHighlightTitle: {
        marginTop: 4,
        color: '#fff',
        fontFamily: 'Bitter',
        fontSize: 27,
        lineHeight: 32,
        fontWeight: '700',
    },
    findifyPoiContent: {
        paddingHorizontal: 24,
        paddingTop: 24,
        paddingBottom: BottomNavigationPadding + 118,
    },
    findifyPoiContentTablet: {
        width: '100%',
        maxWidth: 820,
        alignSelf: 'center',
        paddingHorizontal: 40,
        paddingTop: 34,
    },
    findifyPoiContentLandscapeTablet: {
        maxWidth: 980,
    },
    findifyPoiTitleCard: {
        backgroundColor: 'rgba(232, 229, 222, 0.72)',
        borderLeftWidth: 4,
        borderLeftColor: '#990000',
        paddingHorizontal: 16,
        paddingVertical: 14,
        marginBottom: 24,
        borderTopRightRadius: 8,
        borderBottomRightRadius: 8,
    },
    findifyPoiIntroCard: {
        borderLeftWidth: 4,
        paddingHorizontal: 18,
        paddingVertical: 16,
        marginBottom: 26,
        borderTopRightRadius: 14,
        borderBottomRightRadius: 14,
    },
    findifyPoiTitle: {
        color: '#990000',
        fontFamily: 'Bitter',
        fontSize: 29,
        lineHeight: 34,
        fontWeight: '700',
    },
    findifyPoiSubtitle: {
        marginTop: 4,
        color: '#3a2a28',
        fontSize: 17,
        lineHeight: 23,
        fontStyle: 'italic',
    },
    findifyPoiLanguageSection: {
        marginBottom: 28,
    },
    findifyPoiEnglishSection: {
        borderTopWidth: 1,
        borderTopColor: 'rgba(153, 0, 0, 0.22)',
        paddingTop: 20,
    },
    findifyPoiLanguageTitleRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        marginBottom: 10,
    },
    findifyPoiLanguageTitle: {
        color: '#990000',
        fontSize: 12,
        lineHeight: 16,
        letterSpacing: 3,
        fontWeight: '700',
    },
    findifyPoiParagraph: {
        color: '#241716',
        fontSize: 17,
        lineHeight: 27,
    },
    roomInfoLinkCard: {
        minHeight: 64,
        borderWidth: 1,
        borderRadius: 8,
        paddingHorizontal: 14,
        paddingVertical: 10,
        marginBottom: 28,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
    },
    roomInfoLinkIcon: {
        width: 42,
        height: 42,
        borderRadius: 6,
        alignItems: 'center',
        justifyContent: 'center',
    },
    roomInfoLinkText: {
        flex: 1,
        fontFamily: 'Bitter',
        fontSize: 19,
        lineHeight: 24,
        fontWeight: '700',
    },
    featuredCollectionSection: {
        marginTop: 4,
        marginBottom: 28,
    },
    featuredCollectionTitleRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        marginBottom: 18,
    },
    featuredCollectionLine: {
        flex: 1,
        height: 1,
    },
    featuredCollectionTitle: {
        fontFamily: 'Bitter',
        fontSize: 23,
        lineHeight: 29,
        fontWeight: '700',
        textAlign: 'center',
    },
    featuredObjectRow: {
        minHeight: 84,
        marginBottom: 14,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 14,
    },
    featuredObjectThumbWrap: {
        width: 84,
        height: 84,
        backgroundColor: 'rgba(255,255,255,0.45)',
        overflow: 'hidden',
    },
    featuredObjectThumb: {
        width: '100%',
        height: '100%',
    },
    featuredObjectTitle: {
        flex: 1,
        fontSize: 18,
        lineHeight: 23,
        fontWeight: '600',
    },
    roomInfoModalPage: {
        flex: 1,
    },
    roomInfoModalClose: {
        position: 'absolute',
        top: 64,
        right: 18,
        zIndex: 5,
        width: 44,
        height: 44,
        borderRadius: 22,
        alignItems: 'center',
        justifyContent: 'center',
        shadowColor: '#000',
        shadowOpacity: 0.24,
        shadowRadius: 8,
        shadowOffset: { width: 0, height: 3 },
    },
    roomInfoModalContent: {
        paddingHorizontal: 24,
        paddingTop: 34,
        paddingBottom: BottomNavigationPadding + 56,
    },
    roomInfoModalContentTablet: {
        width: '100%',
        maxWidth: 820,
        alignSelf: 'center',
        paddingHorizontal: 40,
    },
    roomInfoModalTitle: {
        fontFamily: 'Bitter',
        fontSize: 34,
        lineHeight: 40,
        fontWeight: '700',
        marginBottom: 24,
        paddingRight: 48,
    },
    findifyPoiNextCard: {
        marginTop: 2,
        borderRadius: 8,
        borderWidth: 1,
        backgroundColor: '#b00000',
        padding: 20,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 14,
        shadowColor: '#000',
        shadowOpacity: 0.18,
        shadowRadius: 8,
        shadowOffset: { width: 0, height: 4 },
    },
    findifyPoiNextTitle: {
        color: '#fff',
        fontFamily: 'Bitter',
        fontSize: 25,
        lineHeight: 30,
        fontWeight: '700',
    },
    findifyPoiNextCopy: {
        marginTop: 5,
        color: 'rgba(255,255,255,0.85)',
        fontSize: 14,
        lineHeight: 19,
    },
    findifyPoiNextCopyStrong: {
        color: '#f5c443',
        fontWeight: '700',
    },
    findifyPoiNextIcon: {
        width: 54,
        height: 54,
        borderRadius: 27,
        backgroundColor: '#f5c443',
        alignItems: 'center',
        justifyContent: 'center',
    },
    findifyDetailsGallery: {
        marginTop: 6,
    },
    findifyVideoModal: {
        flex: 1,
        backgroundColor: 'rgba(0,0,0,0.92)',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 18,
    },
    findifyVideoClose: {
        position: 'absolute',
        top: 18,
        right: 18,
        zIndex: 10,
        width: 46,
        height: 46,
        borderRadius: 23,
        backgroundColor: 'rgba(153, 0, 0, 0.9)',
        alignItems: 'center',
        justifyContent: 'center',
    },
    reviewPage: {
        flex: 1,
        backgroundColor: '#f9f7f2',
    },
    reviewHeader: {
        height: 56,
        backgroundColor: '#f9f7f2',
        borderBottomWidth: 1,
        borderBottomColor: 'rgba(226, 177, 60, 0.45)',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 28,
    },
    reviewHeaderButton: {
        width: 40,
        height: 40,
        alignItems: 'center',
        justifyContent: 'center',
    },
    reviewLogo: {
        width: 126,
        height: 32,
    },
    reviewContent: {
        paddingHorizontal: 20,
        paddingTop: 48,
        paddingBottom: BottomNavigationPadding + 118,
    },
    reviewContentTablet: {
        width: '100%',
        maxWidth: 760,
        alignSelf: 'center',
        paddingHorizontal: 40,
    },
    reviewContentLandscapeTablet: {
        maxWidth: 980,
    },
    reviewTitleBlock: {
        alignItems: 'center',
        marginBottom: 46,
    },
    reviewTitle: {
        color: '#990000',
        fontFamily: 'Bitter',
        fontSize: 52,
        lineHeight: 58,
        fontWeight: '700',
        textAlign: 'center',
    },
    reviewLead: {
        marginTop: 22,
        color: '#3a1f1d',
        fontSize: 19,
        lineHeight: 26,
        textAlign: 'center',
    },
    reviewLeadItalic: {
        marginTop: 5,
        color: 'rgba(58, 31, 29, 0.74)',
        fontSize: 16,
        lineHeight: 22,
        fontStyle: 'italic',
        textAlign: 'center',
    },
    reviewQuestionCard: {
        minHeight: 204,
        borderWidth: 1,
        borderColor: 'rgba(153, 0, 0, 0.28)',
        borderRadius: 4,
        backgroundColor: 'rgba(255,255,255,0.58)',
        paddingHorizontal: 24,
        paddingVertical: 24,
        marginBottom: 32,
    },
    reviewQuestionTitle: {
        color: '#0f0908',
        fontFamily: 'Bitter',
        fontSize: 28,
        lineHeight: 34,
        fontWeight: '700',
    },
    reviewQuestionSubtitle: {
        marginTop: 6,
        color: '#3a1f1d',
        fontSize: 13,
        lineHeight: 18,
        letterSpacing: 2,
        textTransform: 'uppercase',
    },
    reviewRatingRow: {
        marginTop: 42,
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    reviewRatingButton: {
        width: 42,
        height: 42,
        alignItems: 'center',
        justifyContent: 'center',
    },
    reviewSubmitButton: {
        height: 72,
        borderRadius: 3,
        backgroundColor: '#b00000',
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: 34,
        shadowColor: '#000',
        shadowOpacity: 0.18,
        shadowRadius: 7,
        shadowOffset: { width: 0, height: 4 },
    },
    reviewSubmitText: {
        color: '#fff',
        fontFamily: 'Bitter',
        fontSize: 21,
        lineHeight: 27,
        fontWeight: '700',
        textAlign: 'center',
    },
    reviewSubmitSubtext: {
        color: '#fff',
        fontSize: 13,
        lineHeight: 18,
        fontStyle: 'italic',
        textAlign: 'center',
    },
    reviewBottomImage: {
        width: '100%',
        height: 190,
        borderRadius: 3,
        marginTop: 82,
    },
    routeMapSection: {
        marginTop: 22,
        gap: 12,
    },
    routeMapHeader: {
        flexDirection: 'row',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
    },
    routeMapTitle: {
        fontSize: 22,
        lineHeight: 28,
        fontWeight: 'bold',
    },
    routeMapCount: {
        fontSize: 14,
        lineHeight: 20,
    },
    routeMapContainer: {
        height: 320,
        width: '100%',
        borderRadius: 16,
        overflow: 'hidden',
        backgroundColor: '#d7f5fb',
    },
    routeMap: {
        flex: 1,
    },
    routeMapOverlay: {
        position: 'absolute',
        left: 12,
        right: 12,
        bottom: 12,
        minHeight: 72,
        borderRadius: 12,
        padding: 12,
        paddingRight: 34,
        backgroundColor: 'rgba(255,255,255,0.96)',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        zIndex: 1000,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 3 },
        shadowOpacity: 0.2,
        shadowRadius: 8,
    },
    routeMapOverlayClose: {
        position: 'absolute',
        right: -8,
        top: -8,
        width: 24,
        height: 24,
        borderRadius: 12,
        backgroundColor: '#000',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1001,
    },
    routeMapOverlayNumber: {
        width: 34,
        height: 34,
        borderRadius: 17,
        backgroundColor: '#000',
        borderWidth: 2,
        borderColor: '#ffc42d',
        alignItems: 'center',
        justifyContent: 'center',
    },
    routeMapOverlayNumberText: {
        color: '#fff',
        fontSize: 15,
        lineHeight: 18,
        fontWeight: 'bold',
    },
    routeMapOverlayTitle: {
        fontSize: 15,
        lineHeight: 20,
        fontWeight: '600',
    },
    routeMapOverlaySubtitle: {
        fontSize: 13,
        lineHeight: 18,
        marginTop: 2,
    },
    routeMarker: {
        width: 30,
        height: 30,
        borderRadius: 15,
        backgroundColor: '#000',
        borderWidth: 2,
        borderColor: '#ffc42d',
        alignItems: 'center',
        justifyContent: 'center',
    },
    routeMarkerText: {
        color: '#fff',
        fontSize: 14,
        lineHeight: 18,
        fontWeight: 'bold',
    },
    routePointsList: {
        gap: 8,
    },
    routePointLine: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        paddingVertical: 8,
    },
    routePointNumber: {
        width: 26,
        height: 26,
        borderRadius: 13,
        backgroundColor: '#000',
        borderWidth: 2,
        borderColor: '#ffc42d',
        alignItems: 'center',
        justifyContent: 'center',
    },
    routePointNumberText: {
        color: '#fff',
        fontSize: 13,
        lineHeight: 16,
        fontWeight: 'bold',
    },
    routePointTitle: {
        flex: 1,
        fontSize: 15,
        lineHeight: 20,
    }
})

const getCoordsFromPoint = (pointGeometry: string) => {
    const coordinatesMatch = pointGeometry?.match(/\(([^)]+)\)/);
    if (!coordinatesMatch) return null;

    const coordinatesArray = coordinatesMatch[1].split(' ');
    const latitude = parseFloat(coordinatesArray[0]);
    const longitude = parseFloat(coordinatesArray[1]);

    if (Number.isNaN(latitude) || Number.isNaN(longitude)) return null;
    return { latitude, longitude };
}

const getRoutePoints = (content: any) => {
    return [...(content?.points_of_interest ?? [])]
        .sort((a: any, b: any) => Number(a.sequence ?? 0) - Number(b.sequence ?? 0))
        .map((line: any, index: number) => {
            const poi = line.point_of_interest ?? line;
            const coords = getCoordsFromPoint(poi?.lat_long);
            if (!coords) return null;

            return {
                ...line,
                point: poi,
                index,
                latitude: coords.latitude,
                longitude: coords.longitude,
            }
        })
        .filter(Boolean);
}

const makeRouteLineShape = (points: any[]) => ({
    type: 'Feature',
    geometry: {
        type: 'LineString',
        coordinates: points.map((point: any) => [point.longitude, point.latitude]),
    },
    properties: {},
    eventDetailHero: { height: 360, width: '100%', backgroundColor: '#021E49', position: 'relative', overflow: 'hidden' },
    eventDetailShade: { ...StyleSheet.absoluteFillObject, backgroundColor: 'rgba(2,30,73,.5)' },
    eventDetailTop: { position: 'absolute', top: 24, left: 16, right: 16, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
    calendarIcon: { backgroundColor: '#fff', borderRadius: 24, padding: 12 },
    eventDetailHeroText: { position: 'absolute', bottom: 28, left: 20, right: 20, gap: 8 },
    eventDetailEyebrow: { color: '#E7C58F', fontSize: 11, fontWeight: '900', letterSpacing: 1.3 },
    eventDetailTitle: { color: '#fff', fontSize: 30, lineHeight: 35, fontWeight: '800' },
    eventDetailBody: { marginTop: -20, borderTopLeftRadius: 24, borderTopRightRadius: 24, padding: 20, paddingTop: 24, minHeight: 300 },
    eventDateCard: { flexDirection: 'row', alignItems: 'center', gap: 12, borderRadius: 16, padding: 14, backgroundColor: 'rgba(188,149,94,.14)', marginBottom: 18 },
    eventDateMain: { fontSize: 13, fontWeight: '800' },
    eventDateHint: { color: '#4FA5A1', fontSize: 11, marginTop: 3 },
    calendarButton: { marginLeft: 'auto', borderRadius: 10, paddingHorizontal: 12, paddingVertical: 10 },
    calendarButtonText: { color: '#fff', fontSize: 12, fontWeight: '800' },
    eventAddress: { color: '#4FA5A1', fontSize: 14, fontWeight: '700', marginBottom: 14 },
    eventResume: { color: '#021E49', fontSize: 16, lineHeight: 23, fontWeight: '700', marginBottom: 16 },
});

const makeRouteBounds = (points: any[]) => {
    const latitudes = points.map((point: any) => point.latitude);
    const longitudes = points.map((point: any) => point.longitude);

    return {
        sw: [Math.min(...longitudes), Math.min(...latitudes)],
        ne: [Math.max(...longitudes), Math.max(...latitudes)],
        paddingLeft: 0.00001,
        paddingRight: 0.00001,
        paddingTop: 0.00001,
        paddingBottom: 0.00001,
    };
}

const RouteDetailsMap = ({ content, findifyDataContext, navigation }: any) => {
    const points = useMemo(() => getRoutePoints(content), [content]);
    const [routeShape, setRouteShape] = useState<any>(null);
    const [selectedRoutePoint, setSelectedRoutePoint] = useState<any>(null);

    const fallbackLineShape = useMemo(() => makeRouteLineShape(points), [points]);

    useEffect(() => {
        if (points.length < 2 || content?.geojson) return;

        const coordinates = points.map((point: any) => `${point.longitude},${point.latitude}`).join(';');
        const url = `https://router.project-osrm.org/route/v1/driving/${coordinates}?overview=full&geometries=geojson`;

        fetch(url)
            .then((response) => response.ok ? response.json() : null)
            .then((data) => {
                const geometry = data?.routes?.[0]?.geometry;
                if (!geometry?.coordinates?.length) return;

                setRouteShape({
                    type: 'Feature',
                    geometry,
                    properties: {},
                });
            })
            .catch(() => {
                setRouteShape(null);
            });
    }, [content?.geojson, points]);

    if (points.length === 0) return null;

    const lineShape = content?.geojson
        ? (typeof content.geojson === 'string' ? JSON.parse(content.geojson) : content.geojson)
        : (routeShape ?? fallbackLineShape);
    const bounds = makeRouteBounds(points);
    const centerCoordinate = [
        (bounds.sw[0] + bounds.ne[0]) / 2,
        (bounds.sw[1] + bounds.ne[1]) / 2,
    ];

    return <View style={styles.routeMapSection}>
        <View style={styles.routeMapHeader}>
            <PlaceText style={[styles.routeMapTitle, { color: PlaceColors(findifyDataContext).primary.get() }]}>
                Rota
            </PlaceText>
            <PlaceText style={[styles.routeMapCount, { color: PlaceColors(findifyDataContext).text.withAlpha(180) }]}>
                {points.length} pontos
            </PlaceText>
        </View>
        <View style={styles.routeMapContainer}>
            <RNMapbox.MapView style={styles.routeMap} styleURL={StyleURL.Light}>
                <RNMapbox.Camera
                    bounds={bounds}
                    centerCoordinate={centerCoordinate}
                    zoomLevel={content?.map_zoom ? Number(content.map_zoom) : 15}
                />
                <RNMapbox.ShapeSource id="routeLine" shape={lineShape}>
                    <RNMapbox.LineLayer id="routeLineLayer" sourceID="routeLine" />
                </RNMapbox.ShapeSource>
                {points.map((routePoint: any) => {
                    const title = getTranslatedString(routePoint.point?.title, findifyDataContext);
                    return <RNMapbox.MarkerView
                        key={routePoint.route_point_of_interest_id ?? routePoint.point_of_interest_id}
                        coordinate={[routePoint.longitude, routePoint.latitude]}
                        anchor={{ x: 0.5, y: 0.5 }}
                    >
                        <Pressable
                            onPress={() => {
                                setSelectedRoutePoint(routePoint);
                            }}
                            style={styles.routeMarker}
                        >
                            <PlaceText style={styles.routeMarkerText}>{routePoint.index + 1}</PlaceText>
                        </Pressable>
                    </RNMapbox.MarkerView>
                })}
            </RNMapbox.MapView>
            {!!selectedRoutePoint && <Pressable
                style={styles.routeMapOverlay}
                onPress={() => {
                    navigation.push('PlaceContentDetails', {
                        module: 'points_of_interest',
                        content_id: selectedRoutePoint.point?.point_of_interest_id,
                        page_slug: selectedRoutePoint.point?.page_slug,
                    });
                }}
            >
                <Pressable
                    style={styles.routeMapOverlayClose}
                    onPress={() => setSelectedRoutePoint(null)}
                >
                    <Icon name="close" size={18} color="#fff" />
                </Pressable>
                <View style={styles.routeMapOverlayNumber}>
                    <PlaceText style={styles.routeMapOverlayNumberText}>{selectedRoutePoint.index + 1}</PlaceText>
                </View>
                <View style={{ flex: 1 }}>
                    <PlaceText style={[styles.routeMapOverlayTitle, { color: PlaceColors(findifyDataContext).text.get() }]}>
                        {getTranslatedString(selectedRoutePoint.point?.title, findifyDataContext)}
                    </PlaceText>
                    {!!selectedRoutePoint.point?.address && <PlaceText style={[styles.routeMapOverlaySubtitle, { color: PlaceColors(findifyDataContext).text.withAlpha(170) }]}>
                        {selectedRoutePoint.point.address}
                    </PlaceText>}
                </View>
                <Icon name="chevron-forward" size={20} color={PlaceColors(findifyDataContext).text.withAlpha(140)} />
            </Pressable>}
        </View>
        <View style={styles.routePointsList}>
            {points.map((routePoint: any) => {
                const title = getTranslatedString(routePoint.point?.title, findifyDataContext);
                return <Pressable
                    key={`route_point_${routePoint.route_point_of_interest_id ?? routePoint.point_of_interest_id}`}
                    style={styles.routePointLine}
                    onPress={() => {
                        navigation.push('PlaceContentDetails', {
                            module: 'points_of_interest',
                            content_id: routePoint.point?.point_of_interest_id,
                            page_slug: routePoint.point?.page_slug,
                        });
                    }}
                >
                    <View style={styles.routePointNumber}>
                        <PlaceText style={styles.routePointNumberText}>{routePoint.index + 1}</PlaceText>
                    </View>
                    <PlaceText style={[styles.routePointTitle, { color: PlaceColors(findifyDataContext).text.get() }]}>
                        {title}
                    </PlaceText>
                    <Icon name="chevron-forward" size={18} color={PlaceColors(findifyDataContext).text.withAlpha(120)} />
                </Pressable>
            })}
        </View>
    </View>
}
